import { React, Fragment, useEffect, useContext, useState, useRef } from 'react'
import axios from 'axios';
import { useLocation, useHistory } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react'
import { ExclamationTriangleIcon, DocumentDuplicateIcon, ClipboardIcon } from '@heroicons/react/24/outline'
import { FileText, X, Search } from "lucide-react"

import { AuthContext } from "../../utils/auth.js";
import { ref, onValue } from "firebase/database";
import { db } from '../../utils/firebaseApp.js';

import { PlusIcon, ChevronRightIcon, MapPinIcon } from '@heroicons/react/20/solid'

import ModalForNewExtraction from '../../components/utils/ModalForNewExtraction.js';
import CreateExtraction from '../../components/CreateExtraction.js';

import toast from 'react-hot-toast';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';


function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const statuses = {
    Complete: 'text-green-700 bg-green-50 ring-green-600/20',
    complete: 'text-green-700 bg-green-50 ring-green-600/20',
    'In progress': 'text-gray-600 bg-gray-50 ring-gray-500/10',
    'in progress': 'text-gray-600 bg-gray-50 ring-gray-500/10',
    processing: 'text-gray-600 bg-gray-50 ring-gray-500/10',
    archived: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20',
    Archived: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20',
}

export default function ExtractionsList() {
    const { currentUser } = useContext(AuthContext);
    const [extractions, setExtractions] = useState([]);
    const [isLoadingExtractions, setIsLoadingExtractions] = useState(true);
    const location = useLocation();
    const history = useHistory();

    const adminEmails = ['barzoiu.alexandru@gmail.com', "alexraducu03@gmail.com"];
    const isAdmin = adminEmails.includes(currentUser.email);

    const [showImport, setShowImport] = useState(false);
    const [importType, setImportType] = useState('');

    const [adminUserId, setAdminUserId] = useState('');
    const [searchedUserId, setSearchedUserId] = useState('');

    // Load admin user ID from URL on mount/change
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const adminUserIdParam = queryParams.get('adminUserId');

        if (isAdmin && adminUserIdParam) {
            setSearchedUserId(adminUserIdParam);
            setAdminUserId(adminUserIdParam);
        }
    }, [isAdmin, location.search]);

    useEffect(() => {
        // Handle query parameters for import type
        const queryParams = new URLSearchParams(location.search);
        const param = queryParams.get('type');

        if (param) {
            // Remove the parameter from URL without navigation
            queryParams.delete('type');
            const newUrl = queryParams.toString()
                ? `${location.pathname}?${queryParams.toString()}`
                : location.pathname;
            history.replace(newUrl);

            setImportType(param);
            setShowImport(true);
        }

        // Use searchedUserId if admin has searched, otherwise use current userId
        const targetUserId = (isAdmin && searchedUserId) ? searchedUserId : currentUser.uid;
        let userId = targetUserId;

        // Maintain listeners for real-time updates
        const extractionsRef = ref(db, `extractions/${userId}`);
        const batchesRef = ref(db, `batches/${userId}`);
        setIsLoadingExtractions(true);

        // Store unsubscribe functions
        let batchesListener = () => { };
        const extractionsListener = onValue(extractionsRef, (extractionsSnapshot) => {
            const extractionsData = extractionsSnapshot.val();

            batchesListener();
            batchesListener = onValue(batchesRef, (batchesSnapshot) => {
                const batchesData = batchesSnapshot.val();

                if (extractionsData) {
                    const tempExtractions = Object.keys(extractionsData).map((extractionId) => {
                        const extraction = extractionsData[extractionId];
                        const extractionBatches = batchesData && batchesData[extractionId] ? batchesData[extractionId] : {};

                        // Determine the extraction status based on batches
                        let extractionStatus = 'complete';
                        if (extractionBatches) {
                            Object.values(extractionBatches).forEach((batch) => {
                                if (batch.status === 'processing') {
                                    extractionStatus = 'processing';
                                }
                            });
                        }

                        return {
                            name: extraction.name ?? 'undefined',
                            description: extraction.description ?? '',
                            status: extractionStatus,
                            language: extraction.language,
                            noOfBatches: extractionBatches ? Object.keys(extractionBatches).length : 0,
                            noOfFields: extraction.fields ? Object.keys(extraction.fields).length : 0,
                            batches: extractionBatches,
                            fields: extraction.fields,
                            id: extractionId,
                            createdAt: extraction.createdAt ?? Date.now(),
                            workflowId: extraction.workflowId ?? '',
                            pinned: extraction.pinned ?? false,
                        };
                    });

                    let sortedExtractions = tempExtractions.sort((a, b) => Number(b.createdAt) - Number(a.createdAt));
                    sortedExtractions = sortedExtractions.sort((a, b) => Number(b.pinned) - Number(a.pinned));

                    setExtractions(sortedExtractions);
                } else {
                    setExtractions([]);
                }
                setIsLoadingExtractions(false);
            }, () => {
                setIsLoadingExtractions(false);
            });
        }, () => {
            setIsLoadingExtractions(false);
        });

        // Cleanup listeners on component unmount
        return () => {
            extractionsListener();
            batchesListener();
        };
    }, [currentUser.uid, searchedUserId, isAdmin, location.search, history]);

    const [open, setOpen] = useState(false);

    const serverUrl = process.env.REACT_APP_SERVER_URL;
    const environment = process.env.REACT_APP_ENV;

    const deleteExtraction = (extractionId) => {
        currentUser.getIdToken().then((idToken) => {
            const url = serverUrl + "/delete";

            axios.post(url, {
                "extractionId": extractionId,
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "Access-Control-Allow-Origin": "*",
                    Authorization: `Bearer ${idToken}`
                }
            }).then((response) => {
                if (response.status === 200) {
                    toast.success("Extraction deleted successfully");
                } else {
                    toast.error("Extraction deletion failed");
                }
            }).catch((error) => {
                toast.error("Extraction deletion failed");
            });
        });
    }

    const pinExtraction = async (extractionId) => {
        await currentUser.getIdToken().then(async (idToken) => {
            const url = serverUrl + "/pinExtraction";

            await axios.post(url, {
                "extractionId": extractionId,
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "Access-Control-Allow-Origin": "*",
                    Authorization: `Bearer ${idToken}`
                }
            }).then((response) => {
                if (response.status === 200) {
                    toast.success("Extraction pinned successfully");
                } else {
                    toast.error("Extraction pinning failed");
                }
            }).catch((error) => {
                toast.error("Extraction pinning failed: " + error.response.data);
            });
        });
    }

    const unpinExtraction = async (extractionId) => {
        await currentUser.getIdToken().then(async (idToken) => {
            const url = serverUrl + "/unpinExtraction";

            await axios.post(url, {
                "extractionId": extractionId,
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "Access-Control-Allow-Origin": "*",
                    Authorization: `Bearer ${idToken}`
                }
            }).then((response) => {
                if (response.status === 200) {
                    toast.success("Extraction unpinned successfully");
                } else {
                    toast.error("Extraction unpinning failed");
                }
            }).catch((error) => {
                toast.error("Extraction unpinning failed");
            });
        });
    }

    const importExtraction = async () => {
        try {
            await currentUser.getIdToken().then(async (idToken) => {
                const url = serverUrl + "/importExtractionByDocumentId";

                const response = await axios.post(url, {
                    type: importType,
                }, {
                    headers: {
                        "Content-Type": "application/json",
                        "Access-Control-Allow-Origin": "*",
                        Authorization: `Bearer ${idToken}`
                    }

                });

                if (response.status === 200) {
                    toast.success('Extraction imported successfully!');
                    return true;
                } else {
                    toast.error('Unable to import extraction!');
                    return false;
                }
            });
        } catch (error) {
            toast.error('Unable to import extraction!');
        }
    }

    const handleAdminSearch = () => {
        if (adminUserId.trim()) {
            setSearchedUserId(adminUserId.trim());
            // Add adminUserId to URL
            const queryParams = new URLSearchParams(location.search);
            queryParams.set('adminUserId', adminUserId.trim());
            history.replace(`${location.pathname}?${queryParams.toString()}`);
        }
    };

    const handleAdminSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAdminSearch();
        }
    };

    const handleClearSearch = () => {
        setAdminUserId('');
        setSearchedUserId('');
        // Remove adminUserId from URL
        const queryParams = new URLSearchParams(location.search);
        queryParams.delete('adminUserId');
        const newUrl = queryParams.toString()
            ? `${location.pathname}?${queryParams.toString()}`
            : location.pathname;
        history.replace(newUrl);
    };

    return (
        <div>
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-base font-semibold leading-6 text-gray-900">Extractions</h1>
                    <p className="mt-1 text-sm text-gray-700">
                        A list of all the extractions that have been uploaded.
                    </p>
                </div>
                <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                    <button
                        onClick={() => setOpen(true)}
                        className="ml-2 inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                        <PlusIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                        New extraction
                    </button>
                </div>
            </div>

            {/* Admin Search */}
            {isAdmin && (
                <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center rounded-md bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-700">
                            Admin
                        </span>
                        <h3 className="text-sm font-semibold text-indigo-900">View extractions for any user</h3>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <input
                            type="text"
                            value={adminUserId}
                            onChange={(e) => setAdminUserId(e.target.value)}
                            onKeyPress={handleAdminSearchKeyPress}
                            placeholder="Enter User ID"
                            className="flex-1 min-w-[200px] text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <button
                            onClick={handleAdminSearch}
                            disabled={!adminUserId.trim()}
                            className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Search className="h-4 w-4" />
                            Search
                        </button>
                        {searchedUserId && (
                            <button
                                onClick={handleClearSearch}
                                className="inline-flex items-center gap-x-1.5 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    {searchedUserId && (
                        <p className="mt-2 text-sm text-indigo-700">
                            Viewing extractions for user: <span className="font-mono font-semibold">{searchedUserId}</span>
                        </p>
                    )}
                </div>
            )}

            <ModalForNewExtraction open={open} setOpen={setOpen} close={() => { }}>
                <CreateExtraction
                    closeModal={(extractionId) => {
                        setOpen(false);
                        if (extractionId) {
                            window.location.href = "/extractions/" + extractionId;
                        } else {
                            window.location.reload();
                        }
                    }}
                    extractionsLength={extractions.length}
                />
            </ModalForNewExtraction>

            <ShowImportModal
                templateType={importType}
                open={showImport}
                setOpen={setShowImport}
                importFunction={async () => {
                    await importExtraction();
                    setShowImport(false);
                }}
            />

            <ul className="divide-y mt-6 divide-gray-100 overflow-hidden bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
                {isLoadingExtractions && (
                    Array.from({ length: 3 }).map((_, index) => (
                        <li key={`extraction-skeleton-${index}`} className="px-4 py-5 sm:px-6">
                            <div className="flex justify-between gap-x-6">
                                <div className="min-w-0 flex-auto">
                                    <Skeleton width={180} height={20} />
                                    <div className="mt-2">
                                        <Skeleton width={260} height={14} />
                                    </div>
                                </div>
                                <Skeleton circle width={20} height={20} />
                            </div>
                        </li>
                    ))
                )}

                {!isLoadingExtractions && extractions.map((extraction) => {
                    // Preserve adminUserId in navigation URL
                    const extractionUrl = searchedUserId
                        ? `/extractions/${extraction.id}?adminUserId=${searchedUserId}`
                        : `/extractions/${extraction.id}`;

                    return (
                        <li key={extraction.id} className="cursor-pointer gap-x-6 hover:bg-gray-50">
                            <a href={extractionUrl} className='px-4 py-5 sm:px-6 relative flex justify-between'>
                                <div className="flex min-w-0 gap-x-4">
                                    <div className="min-w-0 flex-auto">
                                        <div className="flex items-start">
                                            {extraction.pinned && (
                                                <p className="text-sm font-semibold leading-6 text-gray-900 mr-2">
                                                    <MapPinIcon className="h-5 w-5 text-gray-400" />
                                                </p>
                                            )}
                                            <p className="text-sm font-semibold leading-6 text-gray-900 mr-3">
                                                {extraction.name}
                                            </p>
                                            <p className={classNames(
                                                statuses[extraction.status],
                                                'rounded-md whitespace-nowrap mt-0.5 px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset'
                                            )}>
                                                {extraction.status}
                                            </p>
                                        </div>
                                        <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500">
                                            {(extraction.description !== null && extraction.description.length > 0) &&
                                                <>
                                                    <p className="whitespace-nowrap">
                                                        {extraction.description}
                                                    </p>
                                                    <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
                                                        <circle cx={1} cy={1} r={1} />
                                                    </svg>
                                                </>
                                            }
                                            <p className="truncate">
                                                {extraction.noOfBatches} {extraction.noOfBatches === 1 ? 'Batch' : 'Batches'}
                                            </p>
                                            <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
                                                <circle cx={1} cy={1} r={1} />
                                            </svg>
                                            <p className="truncate">
                                                {extraction.noOfFields} {extraction.noOfFields === 1 ? 'Field' : 'Fields'}
                                            </p>
                                            {environment === 'development' && extraction.workflowId &&
                                                <>
                                                    <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
                                                        <circle cx={1} cy={1} r={1} />
                                                    </svg>
                                                    <p>workflowId: {extraction.workflowId}</p>
                                                </>
                                            }
                                        </div>
                                        {(environment === 'developments' || isAdmin) &&
                                            <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500">
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        extraction.pinned ? unpinExtraction(extraction.id) : pinExtraction(extraction.id);
                                                    }}
                                                    className="text-gray-400 hover:text-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                                                >
                                                    {extraction.pinned ? 'Unpin' : 'Pin'}
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        deleteExtraction(extraction.id);
                                                    }}
                                                    className="text-gray-400 hover:text-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        }
                                    </div>
                                </div>
                                <div className="flex shrink-0 items-center gap-x-4">
                                    <ChevronRightIcon className="h-5 w-5 flex-none text-gray-400" aria-hidden="true" />
                                </div>
                            </a>
                        </li>
                    );
                })}
            </ul>

            {!isLoadingExtractions && extractions.length === 0 &&
                <div className="mt-4 flex-1 flex flex-col items-center justify-center h-full rounded-lg bg-gray-100 p-8">
                    <div className="text-center">
                        <h2 className="text-xl font-semibold text-gray-900">No extractions yet</h2>
                        <p className="mt-1.5 text-sm text-gray-600">
                            Get started by creating a new extraction.
                        </p>
                    </div>
                </div>
            }
        </div>
    )
}

function ShowImportModal({ open, setOpen, importFunction, templateType }) {
    const importButtonRef = useRef(null)

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" initialFocus={importButtonRef} onClose={setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-100 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="flex-col items-start">
                                        <div className="flex items-center gap-3 mb-5">
                                            <div className="bg-[#4F46E5]/10 p-2 rounded-lg">
                                                <FileText className="h-5 w-5 text-[#4F46E5]" />
                                            </div>
                                            <h2 className="text-lg font-semibold text-gray-900">Import Extraction Template</h2>
                                        </div>

                                        <div className="mb-3">
                                            <p className="text-sm font-medium text-gray-500 mb-2">Template</p>
                                            <div className="bg-[#F9F7FF] border border-[#E0D7FF] rounded-lg p-4 relative overflow-hidden">
                                                <div className="absolute top-0 left-0 w-1 h-full bg-[#4F46E5]"></div>
                                                <div className="flex items-center">
                                                    <div className="flex-1">
                                                        <p className="text-base font-medium text-gray-900">{templateType}</p>
                                                        <p className="text-xs text-gray-500 mt-0.5">Extraction template</p>
                                                    </div>
                                                    <div className="bg-white p-1.5 rounded-full border border-[#E0D7FF]">
                                                        <FileText className="h-4 w-4 text-[#4F46E5]" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <p className="text-sm text-gray-600 mb-3">
                                            Do you want to import this extraction template to your account?
                                        </p>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <button
                                        type="button"
                                        className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                                        onClick={() => importFunction()}
                                        ref={importButtonRef}
                                    >
                                        Import
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                        onClick={() => setOpen(false)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}